import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  TAB_DEFAULT_CLIENT_TYPE,
  buildClientsListFilter,
  clientListSearchSchema,
  clientQueries,
  parseClientSort,
  type ClientListSearch,
  type MerchantsTab,
} from '@/entities/client';
import { MerchantsPage } from '@/pages/merchants';

/**
 * Merchants route — URL is the single source of truth for all three tabs.
 * Every tab serves the same legacy clients API through `c`-prefixed params;
 * the tab only pins the dataset (`merchants` = businesses, `clients` =
 * vendors, `employees` = staff), so `ctype` is synced on tab switch and
 * never edited inside a tab. Prefetch is best-effort and non-blocking; the
 * shared table widget surfaces its own loading/error state.
 */
export const Route = createFileRoute('/merchants')({
  staticData: { permission: 'merchants.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: clientListSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const clientType = TAB_DEFAULT_CLIENT_TYPE[deps.tab];
    const filter = buildClientsListFilter({
      values: deps,
      clientType,
      businessType: deps.cbiz,
      sort: parseClientSort(deps.csort),
    });
    void context.queryClient
      .prefetchQuery(clientQueries.list(clientType, filter))
      .catch(() => undefined);
  },
  component: MerchantsRoute,
});

function MerchantsRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const patchSearch = (next: Partial<ClientListSearch>) => {
    void navigate({
      search: (prev) => ({ ...prev, ...next }),
    });
  };

  const onClientParamsChange = (next: Partial<ClientListSearch>) => {
    patchSearch(next);
  };

  return (
    <MerchantsPage
      tab={search.tab}
      onTabChange={(tab: MerchantsTab) =>
        patchSearch({
          tab,
          ctype: TAB_DEFAULT_CLIENT_TYPE[tab],
          cpage: 1,
          client_id: undefined,
        })
      }
      clientParams={search}
      onClientParamsChange={onClientParamsChange}
      onOpenOrder={(id) =>
        void navigate({ to: '/orders/$orderId', params: { orderId: String(id) } })
      }
    />
  );
}
